import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/ui/password-input";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, ShieldAlert, Trash2, RefreshCw, Loader2, KeyRound, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function ProductionResetPage() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [keepUsers, setKeepUsers] = useState(true);
  const [showPwd, setShowPwd] = useState(false);
  const [done, setDone] = useState(null);

  // Change my password panel
  const [current, setCurrent] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [pwdSaving, setPwdSaving] = useState(false);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/production/collections");
      setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadPreview(); }, []);

  const doReset = async () => {
    if (confirmPhrase !== "RESET-MAZID-PROD") {
      toast.error("Phrase de confirmation incorrecte");
      return;
    }
    if (!adminPassword) {
      toast.error("Mot de passe requis");
      return;
    }
    if (!window.confirm("Cette action est IRRÉVERSIBLE. Toutes les données opérationnelles seront effacées. Continuer ?")) return;
    setResetting(true);
    try {
      const { data } = await api.post("/production/reset-app", {
        admin_password: adminPassword,
        confirm_phrase: confirmPhrase,
        keep_users: keepUsers,
      });
      setDone(data);
      toast.success(`Application réinitialisée. ${data.total_deleted} documents effacés.`);
      setConfirmPhrase(""); setAdminPassword("");
      loadPreview();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setResetting(false); }
  };

  const changeMyPassword = async () => {
    if (newPwd.length < 8) return toast.error("8 caractères minimum");
    setPwdSaving(true);
    try {
      await api.post(`/production/change-my-password?current_password=${encodeURIComponent(current)}&new_password=${encodeURIComponent(newPwd)}`);
      toast.success("Mot de passe changé. Reconnectez-vous.");
      setCurrent(""); setNewPwd("");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setPwdSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="prod-reset-page">
      <div>
        <div className="chip mb-2"><ShieldAlert className="h-3 w-3" /><span>ZONE DANGEREUSE — SUPER ADMIN</span></div>
        <h1 className="font-heading font-extrabold text-3xl text-red-800">Réinitialisation & mot de passe</h1>
        <p className="text-slate-500 mt-1 max-w-2xl">
          Effacez toutes les données opérationnelles (élèves, notes, paiements, signalements…) avant passage en production.
          Les comptes staff sont conservés par défaut.
        </p>
      </div>

      {/* Section 1 : Changer mon mot de passe */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <div className="font-heading font-bold text-[#1E3A8A] mb-4 flex items-center gap-2">
          <KeyRound className="h-5 w-5" />Changer mon mot de passe
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label>Mot de passe actuel</Label>
            <PasswordInput value={current} onChange={e => setCurrent(e.target.value)} data-testid="pwd-current" />
          </div>
          <div>
            <Label>Nouveau (8+ car.)</Label>
            <PasswordInput value={newPwd} onChange={e => setNewPwd(e.target.value)} data-testid="pwd-new" />
          </div>
          <Button onClick={changeMyPassword} disabled={pwdSaving} className="self-end bg-[#166534] hover:bg-[#14532d] text-white gap-2" data-testid="pwd-save">
            {pwdSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4" />Changer</>}
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          💡 Pour changer les mots de passe d&apos;autres utilisateurs, utilisez la page <a href="/dashboard/utilisateurs" className="text-[#1E3A8A] underline">Utilisateurs &amp; rôles</a>.
        </p>
      </div>

      {/* Section 2 : Preview */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="font-heading font-bold text-[#1E3A8A] flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />État actuel des données
          </div>
          <Button variant="outline" size="sm" onClick={loadPreview} disabled={loading}><RefreshCw className={`h-4 w-4 mr-1 ${loading && "animate-spin"}`} />Actualiser</Button>
        </div>
        {preview && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-xs uppercase text-slate-500">Documents à effacer</div>
                <div className="text-2xl font-extrabold text-red-700">{preview.total_documents_to_delete}</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-xs uppercase text-slate-500">Utilisateurs (préservés)</div>
                <div className="text-2xl font-extrabold text-[#1E3A8A]">{preview.users_total}</div>
              </div>
            </div>
            {preview.collections_to_reset.length > 0 && (
              <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0"><tr>
                    <th className="px-3 py-2 text-left text-xs uppercase text-slate-500">Collection</th>
                    <th className="px-3 py-2 text-right text-xs uppercase text-slate-500">Documents</th>
                  </tr></thead>
                  <tbody>
                    {preview.collections_to_reset.map(c => (
                      <tr key={c.collection} className="border-t border-slate-100">
                        <td className="px-3 py-1.5 font-mono text-xs">{c.collection}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{c.documents}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {preview.collections_to_reset.length === 0 && (
              <div className="text-center py-6 text-slate-400 text-sm">✨ Base de données déjà propre — prête pour la production.</div>
            )}
          </>
        )}
      </div>

      {/* Section 3 : Reset form */}
      <div className="bg-red-50 border-2 border-red-300 rounded-2xl p-6 shadow-sm">
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
          <div>
            <div className="font-heading font-bold text-red-900 text-lg">Réinitialiser l&apos;application</div>
            <p className="text-sm text-red-800 mt-1">
              Cette action est <strong>IRRÉVERSIBLE</strong>. Elle efface toutes les données opérationnelles (élèves, notes, paiements, signalements, pointages, etc.) mais conserve les comptes utilisateurs.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <Label>Phrase de confirmation (tapez exactement <code className="bg-white px-1 rounded font-mono text-xs">RESET-MAZID-PROD</code>)</Label>
            <Input value={confirmPhrase} onChange={e => setConfirmPhrase(e.target.value)} placeholder="RESET-MAZID-PROD" className="font-mono" data-testid="confirm-phrase" />
          </div>
          <div>
            <Label>Votre mot de passe Super Admin</Label>
            <div className="relative">
              <Input type={showPwd ? "text" : "password"} value={adminPassword} onChange={e => setAdminPassword(e.target.value)} data-testid="admin-pwd" />
              <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">
                {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={keepUsers} onChange={e => setKeepUsers(e.target.checked)} data-testid="keep-users" />
            Conserver les comptes utilisateurs (recommandé)
          </label>

          <Button onClick={doReset} disabled={resetting || confirmPhrase !== "RESET-MAZID-PROD" || !adminPassword}
                  className="w-full bg-red-600 hover:bg-red-700 text-white gap-2 py-6" data-testid="btn-reset">
            {resetting ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Trash2 className="h-5 w-5" />RÉINITIALISER MAINTENANT</>}
          </Button>
        </div>
      </div>

      {done && (
        <div className="bg-green-50 border border-green-300 rounded-2xl p-4" data-testid="reset-done">
          <div className="font-bold text-green-900">✅ Réinitialisation effectuée</div>
          <div className="text-sm text-green-800 mt-1">
            {done.total_deleted} documents effacés. Base de données prête pour la production.
          </div>
        </div>
      )}
    </div>
  );
}
